import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { CompanySearchResponse } from '@marstein-radar/shared';
import { buttonStyle, formatEur, formatEurCompact, riskBand, riskWord } from '../styles';
import { useAuth } from '../auth/AuthContext';

// Skratky pre najčastejšie právne formy (user feedback 2026-07-19: plné oficiálne
// názvy v multi-selecte boli neprehľadné) — zoznam je zoradený podľa početnosti
// (viď data/legal-forms.ts na backende), takže táto mapa pokrýva prevažnú väčšinu
// firiem v registri. Nenamapované kódy (zriedkavé právne formy) padajú späť na
// plný názov — stále čitateľné, len dlhšie.
const LEGAL_FORM_ABBR: Record<string, string> = {
  '112': 's.r.o.',
  '101': 'živnostník (nezap. v OR)',
  '701': 'združenie',
  '105': 'slob. povolanie',
  '121': 'a.s.',
  '995': 'nešpecifikovaná',
  '271': 'spol. vlastníkov bytov',
  '103': 'samost. hosp. roľník',
  '102': 'živnostník (zap. v OR)',
  '120': 'n.o. (verej. prospešná)',
  '205': 'družstvo',
  '321': 'rozpočtová organizácia',
  '141': 'poľovnícka organizácia',
  '721': 'cirkevná organizácia',
  '272': 'pozemkové spoločenstvo',
  '801': 'obec / mesto',
  '001': 'SZČO',
  '111': 'v.o.s.',
  '751': 'záujmové združ. PO',
  '301': 'štátny podnik',
  '113': 'k.s.',
  '117': 'nadácia',
  '119': 'nezisková organizácia',
  '118': 'neinvestičný fond',
  '005': 'zahr. podnik (org. zložka)',
  '125': 'j.s.a.',
  '331': 'príspevková organizácia',
  '382': 'verejnoprávna inštitúcia',
  '123': 'SE (európska spoločnosť)',
  '711': 'polit. strana/hnutie',
  '421': 'zahraničná osoba',
  '002': 'organizačná zložka',
  '745': 'komora',
  '741': 'profesná komora',
  '008': 'obecný úrad',
  '803': 'samosprávny kraj',
  '003': 'iná právnická osoba',
};

// Bežné kategórie predmetu podnikania — návrhy do <datalist>, aby sa dalo
// jednoducho vybrať namiesto vypisovania celého textu (user feedback
// 2026-07-19). Pole ostáva voľný text (fulltext nad RPO `activities`), toto sú
// len návrhy, nie uzavretý zoznam.
const ACTIVITY_SUGGESTIONS = [
  'Stavebníctvo',
  'Reštaurácia a pohostinstvo',
  'Informačné technológie',
  'Doprava a logistika',
  'Veľkoobchod a maloobchod',
  'Poľnohospodárstvo',
  'Účtovníctvo a poradenstvo',
  'Zdravotníctvo',
  'Vzdelávanie',
  'Nehnuteľnosti',
  'Výroba',
  'Cestovný ruch a ubytovanie',
  'Finančné služby',
  'Právne služby',
  'Kultúra a zábava',
  'Upratovacie služby',
  'Kaderníctvo a kozmetika',
  'Automobilový servis',
];

// Odvetvia (SK NACE Rev. 2 sekcie A-U) — user request 2026-07-19 (FinStat
// porovnanie: "Odvetvia" nad podrobným NACE kódom). Zrkadlí backend zoznam
// (data/nace-sections.ts) — statický oficiálny číselník sa nemení, netreba
// preň API round-trip.
const NACE_SECTIONS: { code: string; name: string }[] = [
  { code: 'A', name: 'Poľnohospodárstvo, lesníctvo a rybolov' },
  { code: 'B', name: 'Ťažba a dobývanie' },
  { code: 'C', name: 'Priemyselná výroba' },
  { code: 'D', name: 'Dodávka elektriny, plynu, pary a studeného vzduchu' },
  { code: 'E', name: 'Dodávka vody, čistenie a odvod odpadových vôd, odpady' },
  { code: 'F', name: 'Stavebníctvo' },
  { code: 'G', name: 'Veľkoobchod a maloobchod' },
  { code: 'H', name: 'Doprava a skladovanie' },
  { code: 'I', name: 'Ubytovacie a stravovacie služby' },
  { code: 'J', name: 'Informácie a komunikácia' },
  { code: 'K', name: 'Finančné a poisťovacie činnosti' },
  { code: 'L', name: 'Činnosti v oblasti nehnuteľností' },
  { code: 'M', name: 'Odborné, vedecké a technické činnosti' },
  { code: 'N', name: 'Administratívne a podporné služby' },
  { code: 'O', name: 'Verejná správa a obrana, povinné sociálne zabezpečenie' },
  { code: 'P', name: 'Vzdelávanie' },
  { code: 'Q', name: 'Zdravotníctvo a sociálna pomoc' },
  { code: 'R', name: 'Umenie, zábava a rekreácia' },
  { code: 'S', name: 'Ostatné činnosti' },
  { code: 'T', name: 'Činnosti domácností ako zamestnávateľov' },
  { code: 'U', name: 'Činnosti extrateritoriálnych organizácií a združení' },
];

// "Druh vlastníctva" — odvodená aproximácia, nie oficiálna klasifikácia (RPO ju
// neposkytuje, viď backend data/ownership-type.ts pre presný zdroj kódov).
const OWNERSHIP_TYPE_OPTIONS: { value: string; label: string; title?: string }[] = [
  { value: 'private', label: 'Súkromné (domáce)' },
  { value: 'foreign', label: 'Zahraničné' },
  { value: 'state', label: 'Štátne a verejné' },
  { value: 'cooperative', label: 'Družstevné' },
];

// Vek firmy — rýchly výber pásiem nad existujúcim rok-rozsahom (user request
// 2026-07-19, FinStat porovnanie). Počíta yearFrom/yearTo z aktuálneho roka,
// žiadny nový backend parameter netreba.
const CURRENT_YEAR = new Date().getFullYear();
const AGE_BUCKETS: { label: string; yearFrom: string; yearTo: string }[] = [
  { label: 'Menej ako 1 rok', yearFrom: String(CURRENT_YEAR), yearTo: '' },
  { label: '1 – 3 roky', yearFrom: String(CURRENT_YEAR - 3), yearTo: String(CURRENT_YEAR - 1) },
  { label: '3 – 5 rokov', yearFrom: String(CURRENT_YEAR - 5), yearTo: String(CURRENT_YEAR - 3) },
  { label: '5 – 10 rokov', yearFrom: String(CURRENT_YEAR - 10), yearTo: String(CURRENT_YEAR - 5) },
  { label: 'Viac ako 10 rokov', yearFrom: '', yearTo: String(CURRENT_YEAR - 10) },
];

// Rýchly výber pre finančné rozsahy (user feedback 2026-07-19: manuálne
// vypisovanie súm bolo nepohodlné) — dropdown nastaví od/do naraz, no polia
// pod ním ostávajú editovateľné pre presnú vlastnú hodnotu.
const AMOUNT_PRESETS: { label: string; min: string; max: string }[] = [
  { label: 'Akákoľvek suma', min: '', max: '' },
  { label: 'do 100 000 €', min: '', max: '100000' },
  { label: '100 000 – 500 000 €', min: '100000', max: '500000' },
  { label: '500 000 – 1 mil. €', min: '500000', max: '1000000' },
  { label: '1 – 10 mil. €', min: '1000000', max: '10000000' },
  { label: 'nad 10 mil. €', min: '10000000', max: '' },
];

function AmountRangeField({
  label,
  min,
  max,
  onMin,
  onMax,
}: {
  label: string;
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
}) {
  const matchedPreset = AMOUNT_PRESETS.find((p) => p.min === min && p.max === max);
  return (
    <label className="filter-range-field">
      <span>{label}</span>
      <select
        value={matchedPreset?.label ?? 'custom'}
        onChange={(e) => {
          const preset = AMOUNT_PRESETS.find((p) => p.label === e.target.value);
          if (preset) {
            onMin(preset.min);
            onMax(preset.max);
          }
        }}
      >
        {!matchedPreset && <option value="custom">Vlastný rozsah</option>}
        {AMOUNT_PRESETS.map((p) => (
          <option key={p.label} value={p.label}>
            {p.label}
          </option>
        ))}
      </select>
      <div className="filter-range-inputs">
        <input value={min} onChange={(e) => onMin(e.target.value)} type="number" placeholder="od" />
        <span className="filter-range-sep">–</span>
        <input value={max} onChange={(e) => onMax(e.target.value)} type="number" placeholder="do" />
      </div>
    </label>
  );
}

// Malá "?" ikona s vlastným click-toggle popoverom (Týž. 23 — user feedback
// 2026-07-19: title-atribút reaguje len na hover, kliknutím sa nič neukázalo).
// onBlur zatvára popover keď fokus opustí tlačidlo (klik inde, Tab preč).
function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="field-info-icon"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
      >
        ?
      </button>
      {open && (
        <div className="field-info-popover" role="tooltip">
          {text}
        </div>
      )}
    </span>
  );
}

// Tag input — text pole s <datalist> návrhmi, Enter alebo klik na návrh pridá
// "čip" do zoznamu (Týž. 23 — user feedback 2026-07-19: mesto aj NACE mali
// umožniť výber viacerých hodnôt naraz, nie len jednu). `resolve` premení
// zadaný text na uloženú hodnotu (napr. názov NACE → kód) a vráti null, ak
// text nezodpovedá ničomu platnému — TagInput ho v tom prípade nepridá.
function TagInput({
  label,
  datalistId,
  placeholder,
  suggestions,
  chips,
  onAdd,
  onRemove,
  resolve,
}: {
  label: string;
  datalistId: string;
  placeholder: string;
  suggestions: string[];
  chips: { key: string; label: string }[];
  onAdd: (text: string) => void;
  onRemove: (key: string) => void;
  resolve?: (text: string) => boolean;
}) {
  const [text, setText] = useState('');
  function tryAdd(raw: string) {
    const value = raw.trim();
    if (!value) return;
    if (resolve && !resolve(value)) return;
    onAdd(value);
    setText('');
  }
  return (
    <label className="filter-field filter-field-wide">
      <span>{label}</span>
      <input
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          // Výber z <datalist> vyplní input naraz celým textom zhodným s
          // <option> — to je jediný spoľahlivý spôsob, ako v React odlíšiť
          // "vybral z návrhov" od "napísal písmeno", keďže datalist nemá
          // vlastný select event.
          if (suggestions.includes(v)) tryAdd(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            tryAdd(text);
          }
        }}
        placeholder={placeholder}
        list={datalistId}
      />
      <datalist id={datalistId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      {chips.length > 0 && (
        <div className="filter-tag-group">
          {chips.map((c) => (
            <span key={c.key} className="filter-tag">
              {c.label}
              <button
                type="button"
                onClick={() => onRemove(c.key)}
                aria-label={`Odstrániť ${c.label}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </label>
  );
}

// Multi-select chip skupina — každá hodnota sa prepína nezávisle (Týž. 23:
// user feedback chcel "viac na výber" pre stav firmy aj index spoľahlivosti).
// "Všetky" vždy vyprázdni výber namiesto toho, aby bola len ďalšia hodnota v poli.
function MultiChipGroup<T extends string>({
  options,
  selected,
  onToggle,
  onClear,
}: {
  options: { value: T; label: string; title?: string }[];
  selected: T[];
  onToggle: (v: T) => void;
  onClear: () => void;
}) {
  return (
    <div className="filter-chip-group">
      <button
        type="button"
        className="filter-chip"
        aria-pressed={selected.length === 0}
        onClick={onClear}
      >
        Všetky
      </button>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="filter-chip"
          aria-pressed={selected.includes(o.value)}
          onClick={() => onToggle(o.value)}
          title={o.title}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function toggleInArray<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

// Zoraditeľná hlavička výsledkovej tabuľky (Týž. 27, FinStat porovnanie) —
// klik prepína smer, ak je stĺpec už aktívny; inak nastaví nové pole a
// zostupné triedenie. Šípka ukazuje aktuálny smer len na aktívnom stĺpci.
function SortableHeader<T extends string>({
  field,
  label,
  sortBy,
  sortDir,
  onSort,
  align,
}: {
  field: T;
  label: string;
  sortBy: T;
  sortDir: 'asc' | 'desc';
  onSort: (field: T) => void;
  align?: 'right';
}) {
  const active = sortBy === field;
  return (
    <th
      className={`sortable-th${align === 'right' ? ' num' : ''}`}
      onClick={() => onSort(field)}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      {label}
      <span className="sortable-th-arrow">{active ? (sortDir === 'desc' ? '▼' : '▲') : ''}</span>
    </th>
  );
}

// Číslované stránkovanie (Týž. 27, FinStat porovnanie) — okno okolo aktuálnej
// strany + vždy prvá/posledná, "…" pre medzery, namiesto len Predošlá/Ďalšia.
function pageNumbers(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result: (number | '…')[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const p = sorted[i]!;
    if (i > 0 && p - sorted[i - 1]! > 1) result.push('…');
    result.push(p);
  }
  return result;
}

// Deterministický index 0..n-1 zo stringu (Týž. 27, user feedback 2026-07-19:
// "chceli sme sa nejako odlíšiť od konkurencie") — rovnaké odvetvie má vždy tú
// istú farbu bodky naprieč načítaniami, bez potreby ukladať mapovanie kdekoľvek.
function hashIndex(text: string, mod: number): number {
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) % mod;
  return Math.abs(hash);
}

// Rozšírené vyhľadávanie (Týž. 19) — samostatná, viditeľná stránka namiesto skrytej
// sekcie v Nástrojoch (user feedback 2026-07-12: filtre boli nenájditeľné). Mesto,
// kraj (obec→kraj mapovanie, viď apps/api region.ts), predmet podnikania (fulltext nad
// RPO `activities`) a rok založenia — viď docs/CURRENT.md pre presné obmedzenia
// (NACE štruktúrovaný kód a presné hranice krajov nie sú v dostupných dátach).
// Druhá iterácia (user feedback): výsledky ako poriadna stránkovaná tabuľka, nie
// jednoduchý zoznam tlačidiel jedna firma po druhej.
export function SearchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const { data: regions } = useQuery<string[]>({
    queryKey: ['regions'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/regions');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: Infinity,
  });

  // Týž. 19 — presný NACE kód (hlavná činnosť), enrichment z RPO SQL dumpu (viď
  // docs/CURRENT.md) — nie všetky firmy ho majú (~97 % pokrytie k dátumu enrichmentu).
  const { data: naceCodes } = useQuery<{ code: number; name: string }[]>({
    queryKey: ['nace-codes'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/nace-codes');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: Infinity,
  });

  // Týž. 20 — právna forma (backend + endpoint hotové 2026-07-14, UI dobiehané teraz).
  const { data: legalFormOptions } = useQuery<{ code: string; name: string }[]>({
    queryKey: ['legal-forms'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/legal-forms');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: Infinity,
  });

  // Týž. 23 (user feedback 2026-07-19: chcel návrhy pri písaní + výber viacerých
  // miest naraz) — TagInput s <datalist> zo /companies/cities (cachované 24h,
  // ~4800 miest, rovnaký princíp ako NACE nižšie).
  const { data: cityOptions } = useQuery<string[]>({
    queryKey: ['cities'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/cities');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: 24 * 60 * 60_000,
  });
  const [cities, setCities] = useState<string[]>([]);
  // Klik na kraj v mapke na landing page vedie sem s ?region=... — prevezmeme
  // ako počiatočný filter.
  const [region, setRegion] = useState(() => searchParams.get('region') ?? '');
  const [district, setDistrict] = useState('');

  // Týž. 24 (user feedback 2026-07-19: vybrané mesto a kraj/okres sa dali
  // nastaviť navzájom si odporujúco a ticho vrátiť 0 výsledkov) — kraj/okres pre
  // každé mesto, len na kontrolu rozporu; nič sa nezakazuje, len sa upozorní
  // (user výslovne nechcel, aby mu Kraj/Okres skryl možnosti).
  const { data: cityRegions } = useQuery<
    Record<string, { region: string | null; district: string | null }>
  >({
    queryKey: ['city-regions'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/city-regions');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: 24 * 60 * 60_000,
  });
  const locationMismatchWarning = useMemo(() => {
    if (cities.length === 0 || (!region && !district)) return null;
    const mismatched = cities.filter((c) => {
      const info = cityRegions?.[c];
      if (!info) return false;
      if (region && info.region && info.region !== region) return true;
      if (district && info.district && info.district !== district) return true;
      return false;
    });
    if (mismatched.length === 0) return null;
    return `${mismatched.join(', ')} ${mismatched.length === 1 ? 'nepatrí' : 'nepatria'} do zvoleného kraja/okresu — kombinácia asi vráti 0 výsledkov.`;
  }, [cities, region, district, cityRegions]);

  // Týž. 20+ — okres, kaskádovito zúžený na zvolený kraj (finstat inšpirácia).
  // Vždy dotiahnuté všetkých 79 (staleTime: Infinity, zoznam sa nemení), filter na
  // klientovi podľa `region` — jednoduchšie než druhý endpoint volaný pri každej
  // zmene kraja, a 79 položiek je zanedbateľný payload.
  const { data: allDistricts } = useQuery<string[]>({
    queryKey: ['districts'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/districts');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: Infinity,
  });
  const { data: districtsForRegion } = useQuery<string[]>({
    queryKey: ['districts', region],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/districts?region=${encodeURIComponent(region)}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!region,
    staleTime: Infinity,
  });
  const districtOptions = region ? (districtsForRegion ?? []) : (allDistricts ?? []);

  const [activity, setActivity] = useState('');
  // Týž. 22 (user feedback 2026-07-19: 1168 NACE kódov v natívnom <select> sa
  // "strašne dlho" scrolluje) — text input + <datalist> namiesto veľkého selectu.
  // Týž. 23 (user feedback: chcel vybrať viac činností naraz) — TagInput, `nace`
  // je zoznam vybraných {code, name} párov namiesto jednej hodnoty.
  const naceNameToCode = useMemo(() => {
    const map = new Map<string, number>();
    for (const n of naceCodes ?? []) {
      if (!map.has(n.name)) map.set(n.name, n.code);
    }
    return map;
  }, [naceCodes]);
  const [nace, setNace] = useState<{ code: number; name: string }[]>([]);
  // Týž. 25 (user feedback 2026-07-19: FinStat porovnanie — "Odvetvia" popri
  // podrobnom NACE kóde) — sekcie A-U, viď NACE_SECTIONS vyššie.
  const [naceSections, setNaceSections] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  // Týž. 20+ — presný dátum (backend establishedFrom/establishedTo existoval už
  // skôr, len bez UI) — vedľa existujúcich len-rok polí vyššie, pre presnejšie
  // hľadanie ako finstat "Dátum založenia od-do".
  const [establishedFrom, setEstablishedFrom] = useState('');
  const [establishedTo, setEstablishedTo] = useState('');
  const [latestPeriodYear, setLatestPeriodYear] = useState('');
  // Týž. 25 — "mal niekedy dáta za rok X" (na rozdiel od latestPeriodYear
  // vyššie, ktoré je "posledné dostupné údaje sú za rok X").
  const [dataAvailableYear, setDataAvailableYear] = useState('');
  const [hasRpvs, setHasRpvs] = useState(false);
  const [hasForeignStakeholder, setHasForeignStakeholder] = useState(false);
  // Týž. 21 (prieskum "čo chýba" 2026-07-18) — dáta existovali na profile firmy,
  // len sa podľa nich nedalo filtrovať v hlavnom vyhľadávaní. Týž. 23 (user
  // feedback 2026-07-19: "viac na výber") — pole namiesto jednej hodnoty +
  // 'none' pre firmy bez prideleného indexu.
  const [taxReliabilityIndexes, setTaxReliabilityIndexes] = useState<string[]>([]);
  const [hasDphRegistration, setHasDphRegistration] = useState(false);
  const [hasDphCancellationReason, setHasDphCancellationReason] = useState(false);
  const [riskScoreMin, setRiskScoreMin] = useState('');
  const [riskScoreMax, setRiskScoreMax] = useState('');
  // Týž. 20 — finstat-style filtre nad materializovanými finančnými stĺpcami
  // (backend v CompaniesRepository.filter, len UI dobiehané teraz).
  const [legalForms, setLegalForms] = useState<string[]>([]);
  // Týž. 22/23 (user feedback: "Stav firmy" mal obsahovať aj konkurz/
  // reštrukturalizáciu, neskôr "viacej na výber") — pole namiesto jedného
  // prepínača, hodnoty sa vyhodnotia OR-om na backende (viď statuses v
  // companies.repository.ts), takže "aktívne" + "v reštrukturalizácii" spolu
  // dávajú zmysel (nie sú to vzájomne vylučujúce sa stavy).
  const [statuses, setStatuses] = useState<string[]>([]);
  // Týž. 25 — odvodený "druh vlastníctva" (nie oficiálna klasifikácia, viď
  // OWNERSHIP_TYPE_OPTIONS vyššie), rovnaká OR-medzi-hodnotami logika ako
  // statuses vyššie.
  const [ownershipTypes, setOwnershipTypes] = useState<string[]>([]);
  const [revenueMin, setRevenueMin] = useState('');
  const [revenueMax, setRevenueMax] = useState('');
  const [netIncomeMin, setNetIncomeMin] = useState('');
  const [netIncomeMax, setNetIncomeMax] = useState('');
  const [equityMin, setEquityMin] = useState('');
  const [equityMax, setEquityMax] = useState('');
  const [totalAssetsMin, setTotalAssetsMin] = useState('');
  const [totalAssetsMax, setTotalAssetsMax] = useState('');
  const [ebitdaMin, setEbitdaMin] = useState('');
  const [ebitdaMax, setEbitdaMax] = useState('');
  const [workingCapitalMin, setWorkingCapitalMin] = useState('');
  const [workingCapitalMax, setWorkingCapitalMax] = useState('');
  // Týž. 19+ — "rebríček" firiem so skutočnými (materializovanými) finančnými
  // dátami, user request: krajšie štruktúrovať dáta, top firmy podľa obratu na
  // prvý pohľad, 50/stránku. Bez hasFinancials by triedenie podľa obratu vrátilo
  // hromadne importované firmy bez financií (NULL) v náhodnom poradí.
  //
  // User feedback 2026-07-12: stránka má vždy ukazovať tento rebríček automaticky
  // pri otvorení, nie čakať na kliknutie "Hľadať firmy". User feedback 2026-07-19
  // (1): samostatná "Zoradenie výsledkov" sekcia s prepínačom a select-om bola
  // zbytočná — sortBy zostáva fixná konštanta. User feedback 2026-07-19 (2):
  // "prečo mi ukazuje právne formy, ktoré reálne nemáme" — koreň problému bol
  // práve toto: hasFinancials=true napevno zablokoval napr. živnostníkov (drvivá
  // väčšina nemá RÚZ výkazy vôbec, viď companies.repository.ts), takže VŽDY
  // vrátili 0 bez ohľadu na to, koho user vybral. Teraz sa "len s financiami"
  // vynucuje IBA v predvolenom stave (žiadny iný filter nastavený = landing
  // rebríček najväčších firiem) — akonáhle si niekto zvolí čokoľvek konkrétne,
  // ukáže mu všetky zodpovedajúce firmy, nie len tú podmnožinu s výkazmi.
  const hasOtherFiltersActive =
    cities.length > 0 ||
    !!region ||
    !!district ||
    !!activity.trim() ||
    nace.length > 0 ||
    naceSections.length > 0 ||
    !!q.trim() ||
    !!yearFrom.trim() ||
    !!yearTo.trim() ||
    !!establishedFrom ||
    !!establishedTo ||
    !!latestPeriodYear.trim() ||
    !!dataAvailableYear.trim() ||
    legalForms.length > 0 ||
    statuses.length > 0 ||
    ownershipTypes.length > 0 ||
    hasRpvs ||
    hasForeignStakeholder ||
    !!revenueMin.trim() ||
    !!revenueMax.trim() ||
    !!netIncomeMin.trim() ||
    !!netIncomeMax.trim() ||
    !!equityMin.trim() ||
    !!equityMax.trim() ||
    !!totalAssetsMin.trim() ||
    !!totalAssetsMax.trim() ||
    !!ebitdaMin.trim() ||
    !!ebitdaMax.trim() ||
    !!workingCapitalMin.trim() ||
    !!workingCapitalMax.trim() ||
    taxReliabilityIndexes.length > 0 ||
    hasDphRegistration ||
    hasDphCancellationReason ||
    !!riskScoreMin.trim() ||
    !!riskScoreMax.trim();
  const hasFinancials = !hasOtherFiltersActive;
  // Týž. 27 (user feedback 2026-07-19: FinStat porovnanie — klik na hlavičku
  // stĺpca triedi tabuľku) — skutočný stav namiesto pevnej konštanty, mení sa
  // priamo kliknutím na hlavičku (viď SortableHeader nižšie), nie cez
  // "Hľadať firmy".
  const [sortBy, setSortBy] = useState<
    'revenue' | 'netIncome' | 'totalAssets' | 'riskScore' | 'name' | 'city'
  >('revenue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  function toggleSort(field: typeof sortBy): void {
    if (sortBy === field) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  }
  // Týž. 27 — "Záznamov na stránku" (FinStat porovnanie), predtým pevných
  // 50/20 podľa hasFinancials (viď companies.service.ts pre server-side strop).
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [submittedKey, setSubmittedKey] = useState<string | null>('initial');
  // Voliteľné stĺpce (inšpirované foaf.sk "Stĺpce" pickerom, 2026-07-20) —
  // IČO/Vznik/PSČ nie sú zobrazené v tabuľke by default, ale API ich vracia
  // vždy, takže tu netreba žiadny ďalší dopyt, len podmienené renderovanie.
  const [extraColumns, setExtraColumns] = useState({ ico: false, zip: false });
  function toggleExtraColumn(key: keyof typeof extraColumns): void {
    setExtraColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  }
  // Týž. 27 — checkbox výber vo výsledkoch → "Porovnať vybrané" (max 3, rovnaký
  // limit ako existujúci /nastroje porovnávací nástroj).
  const [selectedIcos, setSelectedIcos] = useState<string[]>([]);
  function toggleSelected(ico: string): void {
    setSelectedIcos((prev) =>
      prev.includes(ico) ? prev.filter((i) => i !== ico) : prev.length < 3 ? [...prev, ico] : prev,
    );
  }

  // Zdieľané medzi /filter dopytom (queryFn nižšie) a CSV exportom (exportCsv) —
  // presne tie isté filtre, len export nemá `page` (jeden shot, server-side
  // orezaný cez CompanyExportService, viď companies.controller.ts export.csv).
  function buildFilterParams(): URLSearchParams {
    const params = new URLSearchParams();
    if (cities.length > 0) params.set('city', cities.join(','));
    if (region) params.set('region', region);
    if (district) params.set('district', district);
    if (activity.trim()) params.set('activity', activity.trim());
    if (nace.length > 0) params.set('nace', nace.map((n) => n.code).join(','));
    if (naceSections.length > 0) params.set('naceSections', naceSections.join(','));
    if (q.trim()) params.set('q', q.trim());
    if (yearFrom.trim()) params.set('yearFrom', yearFrom.trim());
    if (yearTo.trim()) params.set('yearTo', yearTo.trim());
    if (establishedFrom) params.set('establishedFrom', establishedFrom);
    if (establishedTo) params.set('establishedTo', establishedTo);
    if (latestPeriodYear.trim()) params.set('latestPeriodYear', latestPeriodYear.trim());
    if (dataAvailableYear.trim()) params.set('dataAvailableYear', dataAvailableYear.trim());
    if (hasFinancials) params.set('hasFinancials', 'true');
    params.set('sortBy', sortBy);
    params.set('sortDir', sortDir);
    params.set('pageSize', String(pageSize));
    if (legalForms.length > 0) params.set('legalForms', legalForms.join(','));
    if (statuses.length > 0) params.set('statuses', statuses.join(','));
    if (ownershipTypes.length > 0) params.set('ownershipTypes', ownershipTypes.join(','));
    if (hasRpvs) params.set('hasRpvs', 'true');
    if (hasForeignStakeholder) params.set('hasForeignStakeholder', 'true');
    if (revenueMin.trim()) params.set('revenueMin', revenueMin.trim());
    if (revenueMax.trim()) params.set('revenueMax', revenueMax.trim());
    if (netIncomeMin.trim()) params.set('netIncomeMin', netIncomeMin.trim());
    if (netIncomeMax.trim()) params.set('netIncomeMax', netIncomeMax.trim());
    if (equityMin.trim()) params.set('equityMin', equityMin.trim());
    if (equityMax.trim()) params.set('equityMax', equityMax.trim());
    if (totalAssetsMin.trim()) params.set('totalAssetsMin', totalAssetsMin.trim());
    if (totalAssetsMax.trim()) params.set('totalAssetsMax', totalAssetsMax.trim());
    if (ebitdaMin.trim()) params.set('ebitdaMin', ebitdaMin.trim());
    if (ebitdaMax.trim()) params.set('ebitdaMax', ebitdaMax.trim());
    if (workingCapitalMin.trim()) params.set('workingCapitalMin', workingCapitalMin.trim());
    if (workingCapitalMax.trim()) params.set('workingCapitalMax', workingCapitalMax.trim());
    if (taxReliabilityIndexes.length > 0)
      params.set('taxReliabilityIndexes', taxReliabilityIndexes.join(','));
    if (hasDphRegistration) params.set('hasDphRegistration', 'true');
    if (hasDphCancellationReason) params.set('hasDphCancellationReason', 'true');
    if (riskScoreMin.trim()) params.set('riskScoreMin', riskScoreMin.trim());
    if (riskScoreMax.trim()) params.set('riskScoreMax', riskScoreMax.trim());
    return params;
  }

  // Týž. 24 (user feedback 2026-07-19: chcel vidieť počet výsledkov ešte pred
  // kliknutím na "Hľadať firmy") — live náhľad, debounced 400ms nech sa
  // nespúšťa dopyt pri každom jednom stlačenom klávese. Samostatný ľahký
  // /filter-count endpoint (len COUNT, žiadne riadky), viď companies.
  // controller.ts.
  const filterParamsSnapshot = buildFilterParams().toString();
  const [debouncedFilterParams, setDebouncedFilterParams] = useState(filterParamsSnapshot);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilterParams(filterParamsSnapshot), 400);
    return () => clearTimeout(timer);
  }, [filterParamsSnapshot]);
  const { data: previewCount, isFetching: isPreviewCounting } = useQuery<number>({
    queryKey: ['filter-count', debouncedFilterParams],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/filter-count?${debouncedFilterParams}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    // User feedback 2026-07-19: bez zvoleného filtra by to ukázalo počet
    // CELÉHO registra (2M+), čo nemá pre "koľko mi to vyfiltrovalo" žiadny
    // zmysel — náhľad má význam len keď je aspoň jeden filter aktívny.
    enabled: hasOtherFiltersActive,
  });

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // 2026-07-27 (user request: "chcem si vybrať CSV alebo XLSX") — voľba formátu
  // je teraz priamo v otváracom menu tlačidla "Exportovať" (results-export-picker
  // nižšie), nie samostatný <select> vedľa neho.
  const exportPickerRef = useRef<HTMLDetailsElement>(null);

  async function exportResults(format: 'csv' | 'xlsx'): Promise<void> {
    setExportError(null);
    setExporting(true);
    exportPickerRef.current?.removeAttribute('open');
    try {
      const params = buildFilterParams();
      const res = await fetch(`/api/v1/companies/export.${format}?${params.toString()}`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `${res.status} ${res.statusText}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `corpuls-export-${Date.now()}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExporting(false);
    }
  }

  const { data, isFetching } = useQuery<CompanySearchResponse>({
    queryKey: ['company-filter', submittedKey, page, pageSize, sortBy, sortDir],
    queryFn: async () => {
      const params = buildFilterParams();
      params.set('page', String(page));
      const res = await fetch(`/api/v1/companies/filter?${params.toString()}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: submittedKey !== null,
  });

  function submit(): void {
    setPage(1);
    setSubmittedKey(
      [
        cities.join(','),
        region,
        district,
        activity,
        nace.map((n) => n.code).join(','),
        naceSections.join(','),
        q,
        yearFrom,
        yearTo,
        establishedFrom,
        establishedTo,
        latestPeriodYear,
        dataAvailableYear,
        legalForms.join(','),
        statuses.join(','),
        ownershipTypes.join(','),
        hasRpvs,
        hasForeignStakeholder,
        revenueMin,
        revenueMax,
        netIncomeMin,
        netIncomeMax,
        equityMin,
        equityMax,
        totalAssetsMin,
        totalAssetsMax,
        ebitdaMin,
        ebitdaMax,
        workingCapitalMin,
        workingCapitalMax,
        taxReliabilityIndexes.join(','),
        hasDphRegistration,
        hasDphCancellationReason,
        riskScoreMin,
        riskScoreMax,
        Date.now(),
      ].join('|'),
    );
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  // Ovládanie stránkovania — vsadené priamo do `.results-toolbar` nad tabuľkou
  // (spolu s Exportovať/Stĺpce do jedného zarovnaného riadku, user feedback
  // 2026-07-27: predošlé dve samostatné lišty pod sebou pôsobili neusporiadane),
  // a znova ako samostatná lišta pod tabuľkou pre dlhé zoznamy.
  function paginationControls(): ReactNode {
    return (
      <>
        <label className="results-pagesize">
          Záznamov na stránku
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </label>
        {totalPages > 1 && (
          <div className="results-pagination">
            <button type="button" onClick={() => setPage(1)} disabled={page <= 1}>
              «
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              ‹
            </button>
            {pageNumbers(page, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`ellipsis-${i}`} className="results-pagination-ellipsis">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={p === page ? 'active' : ''}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ),
            )}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              ›
            </button>
            <button type="button" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
              »
            </button>
          </div>
        )}
      </>
    );
  }

  // Rizikový stĺpec v tabuľke bol skúšaný cez POST /companies/bulk-risk-score, ale
  // ten robí sekvenčný computeScore() per IČO bez concurrency limitu (viď
  // CompaniesService.bulkRiskScore) — nameraných ~12s/firmu pri bežnej DB záťaži
  // (RÚZ bulk-sync beží súbežne), t.j. 50 firiem na stránke = rádovo minúty. Priamo
  // proti požiadavke na rýchle načítanie, preto vrátené späť. Endpoint treba najprv
  // prerobiť na jeden dávkový SQL dotaz (alebo cachovať skóre v `companies` tabuľke)
  // predtým, než sa dá bezpečne volať zo stránky pri každom načítaní.

  return (
    <main className="page">
      <div className="page-header">
        <h1>Rozšírené vyhľadávanie</h1>
        <p>
          Filtruj firmy podľa mesta, kraja/okresu, predmetu podnikania, financií a ďalších kritérií.
        </p>
      </div>

      <div className="search-filter-panel">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <details className="filter-panel-section" open>
            <summary className="filter-panel-summary">Hľadanie</summary>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <label className="filter-field filter-field-wide">
                <span>Názov firmy alebo IČO</span>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Zadaj názov firmy alebo IČO"
                />
              </label>
              <label className="filter-field filter-field-wide">
                <span>Predmet podnikania</span>
                <input
                  value={activity}
                  onChange={(e) => setActivity(e.target.value)}
                  placeholder="Vyber alebo napíš vlastný"
                  list="activity-suggestions"
                />
                <datalist id="activity-suggestions">
                  {ACTIVITY_SUGGESTIONS.map((a) => (
                    <option key={a} value={a} />
                  ))}
                </datalist>
              </label>
              <label className="filter-field filter-field-wide">
                <span>Odvetvia</span>
                <details className="multiselect-dropdown">
                  <summary className="multiselect-dropdown-trigger">
                    {naceSections.length > 0
                      ? `Vybraných: ${naceSections.length}`
                      : 'Všetky odvetvia'}
                  </summary>
                  <div className="multiselect-dropdown-panel">
                    {NACE_SECTIONS.map((s) => (
                      <label key={s.code} className="multiselect-option">
                        <input
                          type="checkbox"
                          checked={naceSections.includes(s.code)}
                          onChange={() => setNaceSections((prev) => toggleInArray(prev, s.code))}
                        />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </details>
              </label>
              <TagInput
                label="Hlavná činnosť (NACE)"
                datalistId="nace-options"
                placeholder="Začni písať, napr. stavebn, doprav..."
                suggestions={[...naceNameToCode.keys()]}
                chips={nace.map((n) => ({ key: String(n.code), label: n.name }))}
                onAdd={(text) => {
                  const code = naceNameToCode.get(text);
                  if (code == null) return;
                  setNace((prev) =>
                    prev.some((n) => n.code === code) ? prev : [...prev, { code, name: text }],
                  );
                }}
                onRemove={(key) => setNace((prev) => prev.filter((n) => String(n.code) !== key))}
                resolve={(text) => naceNameToCode.has(text)}
              />
            </div>
          </details>

          <details className="filter-panel-section">
            <summary className="filter-panel-summary">Lokalita</summary>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <TagInput
                label="Mesto / obec"
                datalistId="city-options"
                placeholder="Začni písať, napr. Košice..."
                suggestions={cityOptions ?? []}
                chips={cities.map((c) => ({ key: c, label: c }))}
                onAdd={(text) =>
                  setCities((prev) => (prev.includes(text) ? prev : [...prev, text]))
                }
                onRemove={(key) => setCities((prev) => prev.filter((c) => c !== key))}
              />
              <label className="filter-field">
                <span>Kraj</span>
                <select
                  value={region}
                  onChange={(e) => {
                    setRegion(e.target.value);
                    setDistrict(''); // zmena kraja invaliduje predtým zvolený okres
                  }}
                >
                  <option value="">Všetky kraje</option>
                  {regions?.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <label className="filter-field">
                <span>Okres</span>
                <select value={district} onChange={(e) => setDistrict(e.target.value)}>
                  <option value="">Všetky okresy</option>
                  {districtOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {locationMismatchWarning && (
              <p className="filter-warning">⚠ {locationMismatchWarning}</p>
            )}
          </details>

          <details className="filter-panel-section">
            <summary className="filter-panel-summary">Firma a vlastníctvo</summary>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <label className="filter-field filter-field-wide">
                <span>
                  Právna forma{' '}
                  <InfoTooltip text="Skratky bežných právnych foriem (s.r.o., a.s., v.o.s., k.s., družstvo, SZČO...). Podrž kurzor nad položkou v zozname pre celý oficiálny názov." />
                </span>
                <details className="multiselect-dropdown">
                  <summary className="multiselect-dropdown-trigger">
                    {legalForms.length > 0
                      ? `Vybraných: ${legalForms.length}`
                      : 'Všetky právne formy'}
                  </summary>
                  <div className="multiselect-dropdown-panel">
                    {legalFormOptions?.map((lf) => (
                      <label key={lf.code} className="multiselect-option" title={lf.name}>
                        <input
                          type="checkbox"
                          checked={legalForms.includes(lf.code)}
                          onChange={() => setLegalForms((prev) => toggleInArray(prev, lf.code))}
                        />
                        {LEGAL_FORM_ABBR[lf.code] ?? lf.name}
                      </label>
                    ))}
                  </div>
                </details>
              </label>
              <label className="filter-field filter-field-wide">
                <span>Stav firmy</span>
                <MultiChipGroup
                  options={[
                    { value: 'active', label: 'Aktívne' },
                    { value: 'terminated', label: 'Zrušené' },
                    { value: 'konkurz_restrukturalizacia', label: 'Reštrukturalizácia' },
                    { value: 'konkurz_vyrovnanie', label: 'Konkurz (vyrovnanie)' },
                    { value: 'likvidacia', label: 'Likvidácia' },
                    { value: 'znizenie_imania', label: 'Zníženie imania' },
                  ]}
                  selected={statuses}
                  onToggle={(v) => setStatuses((prev) => toggleInArray(prev, v))}
                  onClear={() => setStatuses([])}
                />
              </label>
            </div>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <label className="filter-field filter-field-wide">
                <span>
                  Druh vlastníctva{' '}
                  <InfoTooltip text="Odvodená aproximácia z právnej formy a zahraničného spoločníka — RPO štruktúrovanú kategorizáciu vlastníctva neposkytuje, nejde o oficiálnu klasifikáciu." />
                </span>
                <MultiChipGroup
                  options={OWNERSHIP_TYPE_OPTIONS}
                  selected={ownershipTypes}
                  onToggle={(v) => setOwnershipTypes((prev) => toggleInArray(prev, v))}
                  onClear={() => setOwnershipTypes([])}
                />
              </label>
            </div>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <label className="filter-field filter-field-wide">
                <span>Doplnkové kritériá</span>
                <div className="filter-chip-group">
                  <button
                    type="button"
                    className="filter-chip"
                    aria-pressed={hasRpvs}
                    onClick={() => setHasRpvs((v) => !v)}
                  >
                    So zápisom v RPVS
                  </button>
                  <button
                    type="button"
                    className="filter-chip"
                    aria-pressed={hasForeignStakeholder}
                    onClick={() => setHasForeignStakeholder((v) => !v)}
                    title="Odvodené z adries akcionárov v RPO, nejde o oficiálnu kategorizáciu."
                  >
                    Zahraničný spoločník/akcionár
                  </button>
                  <button
                    type="button"
                    className="filter-chip"
                    aria-pressed={hasDphRegistration}
                    onClick={() => setHasDphRegistration((v) => !v)}
                  >
                    Platca DPH
                  </button>
                  <button
                    type="button"
                    className="filter-chip"
                    aria-pressed={hasDphCancellationReason}
                    onClick={() => setHasDphCancellationReason((v) => !v)}
                    title="Finančná správa eviduje dôvod na zrušenie DPH registrácie — rizikový signál, nemusí byť aktuálne zrušená."
                  >
                    Dôvod na zrušenie DPH
                  </button>
                </div>
              </label>
            </div>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <label className="filter-field filter-field-wide">
                <span>Index daňovej spoľahlivosti</span>
                <MultiChipGroup
                  options={[
                    { value: 'vysoko spoľahlivý', label: 'Vysoko spoľahlivý' },
                    { value: 'spoľahlivý', label: 'Spoľahlivý' },
                    { value: 'menej spoľahlivý', label: 'Menej spoľahlivý' },
                    { value: 'none', label: 'Bez indexu' },
                  ]}
                  selected={taxReliabilityIndexes}
                  onToggle={(v) => setTaxReliabilityIndexes((prev) => toggleInArray(prev, v))}
                  onClear={() => setTaxReliabilityIndexes([])}
                />
              </label>
            </div>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <label className="filter-field filter-field-narrow">
                <span>Rizikové skóre od</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={riskScoreMin}
                  onChange={(e) => setRiskScoreMin(e.target.value)}
                  placeholder="0"
                />
              </label>
              <label className="filter-field filter-field-narrow">
                <span>Rizikové skóre do</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={riskScoreMax}
                  onChange={(e) => setRiskScoreMax(e.target.value)}
                  placeholder="100"
                />
              </label>
            </div>
          </details>

          <details className="filter-panel-section">
            <summary className="filter-panel-summary">Dátum založenia</summary>
            <label className="filter-field filter-field-narrow" style={{ marginTop: 16 }}>
              <span>Vek firmy — rýchly výber</span>
              <select
                value=""
                onChange={(e) => {
                  const bucket = AGE_BUCKETS.find((b) => b.label === e.target.value);
                  if (bucket) {
                    setYearFrom(bucket.yearFrom);
                    setYearTo(bucket.yearTo);
                  }
                }}
              >
                <option value="">Vybrať pásmo...</option>
                {AGE_BUCKETS.map((b) => (
                  <option key={b.label} value={b.label}>
                    {b.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <label className="filter-range-field">
                <span>Rok založenia</span>
                <div className="filter-range-inputs">
                  <input
                    value={yearFrom}
                    onChange={(e) => setYearFrom(e.target.value)}
                    type="number"
                    placeholder="1993"
                  />
                  <span className="filter-range-sep">–</span>
                  <input
                    value={yearTo}
                    onChange={(e) => setYearTo(e.target.value)}
                    type="number"
                    placeholder="2026"
                  />
                </div>
              </label>
              <label className="filter-range-field">
                <span>Presný dátum (voliteľné)</span>
                <div className="filter-range-inputs">
                  <input
                    value={establishedFrom}
                    onChange={(e) => setEstablishedFrom(e.target.value)}
                    type="date"
                  />
                  <span className="filter-range-sep">–</span>
                  <input
                    value={establishedTo}
                    onChange={(e) => setEstablishedTo(e.target.value)}
                    type="date"
                  />
                </div>
              </label>
            </div>
          </details>

          <details className="filter-panel-section">
            <summary className="filter-panel-summary">Financie (rozsah)</summary>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <label className="filter-field filter-field-narrow">
                <span>Posledné údaje za rok</span>
                <input
                  value={latestPeriodYear}
                  onChange={(e) => setLatestPeriodYear(e.target.value)}
                  type="number"
                  placeholder="2025"
                />
              </label>
              <label className="filter-field filter-field-narrow">
                <span>
                  Dostupné údaje za rok{' '}
                  <InfoTooltip text="Nájde firmy, ktoré mali výkaz za tento rok — aj keď to už nie je ich najnovší (na rozdiel od 'Posledné údaje za rok' vľavo)." />
                </span>
                <input
                  value={dataAvailableYear}
                  onChange={(e) => setDataAvailableYear(e.target.value)}
                  type="number"
                  placeholder="2020"
                />
              </label>
            </div>
            <div className="filter-grid" style={{ marginTop: 16 }}>
              <AmountRangeField
                label="Tržby (€)"
                min={revenueMin}
                max={revenueMax}
                onMin={setRevenueMin}
                onMax={setRevenueMax}
              />
              <AmountRangeField
                label="Výsledok hosp. (€)"
                min={netIncomeMin}
                max={netIncomeMax}
                onMin={setNetIncomeMin}
                onMax={setNetIncomeMax}
              />
              <AmountRangeField
                label="Vlastné imanie (€)"
                min={equityMin}
                max={equityMax}
                onMin={setEquityMin}
                onMax={setEquityMax}
              />
              <AmountRangeField
                label="Aktíva (€)"
                min={totalAssetsMin}
                max={totalAssetsMax}
                onMin={setTotalAssetsMin}
                onMax={setTotalAssetsMax}
              />
              <AmountRangeField
                label="EBITDA (€)"
                min={ebitdaMin}
                max={ebitdaMax}
                onMin={setEbitdaMin}
                onMax={setEbitdaMax}
              />
              <AmountRangeField
                label="Čistý pracovný kapitál (€)"
                min={workingCapitalMin}
                max={workingCapitalMax}
                onMin={setWorkingCapitalMin}
                onMax={setWorkingCapitalMax}
              />
            </div>
          </details>

          <div className="filter-panel-section filter-submit-row">
            <button type="submit" className="search-filter-submit">
              Hľadať firmy
            </button>
            {hasOtherFiltersActive && (
              <span className="filter-preview-count">
                {isPreviewCounting
                  ? 'Počítam…'
                  : previewCount != null
                    ? `${previewCount.toLocaleString('sk-SK')} firiem vyhovuje týmto filtrom`
                    : null}
              </span>
            )}
          </div>
        </form>
      </div>

      {isFetching && !data && (
        <div className="results-table-wrap">
          <table className="results-table">
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={8}>
                    <div className="skel line1" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.results.length === 0 && (
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          Žiadne firmy nenájdené pre zadané filtre.
        </div>
      )}
      {data && data.results.length > 0 && (
        <>
          <div className="section-title">
            <h2>
              {hasOtherFiltersActive ? 'Zoznam spoločností' : 'Najväčšie spoločnosti podľa tržieb'}
            </h2>
            {hasOtherFiltersActive && (
              <span className="hint">{data.total.toLocaleString('sk-SK')} firiem spolu</span>
            )}
          </div>
          <div className="results-toolbar">
            <div className="results-toolbar-left">
              {user && (
                <div className="results-toolbar-export">
                  <details ref={exportPickerRef} className="multiselect-dropdown results-export-picker">
                    <summary className="multiselect-dropdown-trigger" style={buttonStyle}>
                      {exporting ? 'Exportujem...' : 'Exportovať'}
                    </summary>
                    <div className="multiselect-dropdown-panel">
                      <button
                        type="button"
                        className="multiselect-option results-export-option"
                        disabled={exporting}
                        onClick={() => void exportResults('csv')}
                      >
                        CSV
                      </button>
                      <button
                        type="button"
                        className="multiselect-option results-export-option"
                        disabled={exporting}
                        onClick={() => void exportResults('xlsx')}
                      >
                        XLSX (Excel)
                      </button>
                    </div>
                  </details>
                  <InfoTooltip text="Max. 2 000 riadkov na export, denná kvóta podľa plánu (Free 2 000 / Pro 20 000)." />
                  {exportError && (
                    <span style={{ color: 'var(--risk-high)' }}>
                      {exportError}
                      {exportError.includes('vyčerpaný') && (
                        <>
                          {' '}
                          <a href="/podmienky">Viac v Obchodných podmienkach →</a>
                        </>
                      )}
                    </span>
                  )}
                </div>
              )}
              {selectedIcos.length > 0 && (
                <div className="results-compare-bar">
                  <span className="hint">Vybraných na porovnanie: {selectedIcos.length}/3</span>
                  {selectedIcos.length >= 2 && (
                    <a
                      className="results-compare-link"
                      href={`/nastroje?compare=${selectedIcos.join(',')}`}
                    >
                      Porovnať vybrané →
                    </a>
                  )}
                  <button
                    type="button"
                    className="results-compare-clear"
                    onClick={() => setSelectedIcos([])}
                  >
                    Zrušiť výber
                  </button>
                </div>
              )}
            </div>
            <div className="results-toolbar-right">
              {paginationControls()}
              <span className="results-toolbar-divider" aria-hidden="true" />
              <details className="multiselect-dropdown results-columns-picker">
                <summary className="multiselect-dropdown-trigger">Stĺpce</summary>
                <div className="multiselect-dropdown-panel">
                  <label className="multiselect-option">
                    <input
                      type="checkbox"
                      checked={extraColumns.ico}
                      onChange={() => toggleExtraColumn('ico')}
                    />
                    IČO
                  </label>
                  <label className="multiselect-option">
                    <input
                      type="checkbox"
                      checked={extraColumns.zip}
                      onChange={() => toggleExtraColumn('zip')}
                    />
                    PSČ
                  </label>
                </div>
              </details>
            </div>
          </div>
          <div className="results-table-wrap">
            <table className="results-table">
              <colgroup>
                <col style={{ width: 36 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="results-checkbox-col" title="Vyber max 3 firmy na porovnanie" />
                  <SortableHeader
                    field="name"
                    label="Firma"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                  {extraColumns.ico && <th className="results-ico-cell">IČO</th>}
                  <SortableHeader
                    field="city"
                    label="Mesto"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                  {extraColumns.zip && <th>PSČ</th>}
                  <th className="num" title="Rok posledných dostupných účtovných dát danej firmy">
                    Obdobie
                  </th>
                  <SortableHeader
                    field="revenue"
                    label="Tržby"
                    align="right"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    field="netIncome"
                    label="Zisk"
                    align="right"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    field="totalAssets"
                    label="Aktíva"
                    align="right"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    field="riskScore"
                    label="Riziko"
                    align="right"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {isFetching &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={`skel-${i}`}>
                      <td
                        colSpan={
                          8 +
                          Number(extraColumns.ico) +
                          Number(extraColumns.zip)
                        }
                      >
                        <div className="skel line1" />
                      </td>
                    </tr>
                  ))}
                {!isFetching &&
                  data.results.map((c) => (
                    <tr
                      key={c.ico}
                      className="results-row"
                      data-risk={c.riskScore != null ? riskBand(c.riskScore) : undefined}
                      onClick={() => navigate(`/firma/${c.ico}`)}
                    >
                      <td className="results-checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIcos.includes(c.ico)}
                          onChange={() => toggleSelected(c.ico)}
                          disabled={!selectedIcos.includes(c.ico) && selectedIcos.length >= 3}
                        />
                      </td>
                      <td className="results-firm-cell">
                        <div className="results-firm-name" title={c.name ?? c.ico}>
                          {c.name ?? c.ico}
                        </div>
                        {c.naceName && (
                          <div className="results-firm-meta" title={c.naceName}>
                            <span className={`results-nace-dot c${hashIndex(c.naceName, 4)}`} />
                            <span className="results-nace-text">{c.naceName}</span>
                          </div>
                        )}
                      </td>
                      {extraColumns.ico && <td className="results-ico-cell">{c.ico}</td>}
                      <td title={c.city ?? undefined}>{c.city ?? '—'}</td>
                      {extraColumns.zip && <td>{c.zip ?? '—'}</td>}
                      <td className="num">{c.latestPeriod ?? '—'}</td>
                      <td
                        className="num"
                        title={c.latestRevenue != null ? formatEur(c.latestRevenue) : undefined}
                      >
                        {formatEurCompact(c.latestRevenue)}
                      </td>
                      <td
                        className="num"
                        title={c.latestNetIncome != null ? formatEur(c.latestNetIncome) : undefined}
                      >
                        {formatEurCompact(c.latestNetIncome)}
                      </td>
                      <td
                        className="num"
                        title={
                          c.latestTotalAssets != null ? formatEur(c.latestTotalAssets) : undefined
                        }
                      >
                        {formatEurCompact(c.latestTotalAssets)}
                      </td>
                      <td className="num">
                        {c.riskScore != null ? (
                          <span
                            className={`pill ${riskBand(c.riskScore)}`}
                            title={riskWord(c.riskScore)}
                          >
                            {c.riskScore}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="results-pagination-bar results-pagination-bar-bottom">
            {paginationControls()}
          </div>
        </>
      )}
    </main>
  );
}
